import { useEffect, useRef, useState } from 'react';

/**
 * DebloatCanvas
 * -------------
 * Renders a deterministic "debloat" preview of the user's uploaded face photo on a <canvas>.
 *
 * Why a canvas (and NOT an AI-generated image)?
 *   - AI image edit models swap the face / shift features. We don't want that.
 *   - This effect operates on the EXACT pixels the user uploaded:
 *       1. The TOP HALF of the image (eyes, nose, mouth, forehead, hair) is copied 1:1 — pixels
 *          stay in their original positions.
 *       2. Below ~55% of image height we apply a per-row horizontal "pinch" toward the center.
 *          The pinch ramps up smoothly from 0 at row ~55% to its max at the very bottom, so the
 *          chin & jaw narrow into a more pointed/angular shape (mimicking weight loss in the
 *          lower face). The transition is gradient — no hard seam.
 *       3. We composite a few soft radial "shadow" gradients in `multiply` blend mode at the
 *          cheek-hollow zones, the temples, and the under-jaw — simulating the contouring/
 *          hollowing that happens when facial fat is lost.
 *
 *   Everything scales with the `goal` prop (0–100). At goal=0 the canvas is identical to the
 *   uploaded photo. At goal=100 the lower face is at maximum (still subtle) slim + max shadow
 *   contour. Eyes & mouth never move regardless of slider value.
 *
 * Props:
 *   src   – data URL or http URL of the uploaded photo
 *   goal  – 0..100 slider value
 *   className – passthrough class for the wrapping element
 */
export default function DebloatCanvas({ src, goal = 0, className = '' }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);

  // Load the source image once when src changes.
  useEffect(() => {
    if (!src) return;
    setImgLoaded(false);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgRef.current = img;
      setImgLoaded(true);
    };
    img.onerror = () => {
      imgRef.current = null;
      setImgLoaded(false);
    };
    img.src = src;
  }, [src]);

  // Re-render whenever the image is ready or the slider value changes.
  useEffect(() => {
    if (!imgLoaded) return;
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;

    // Render at the image's native aspect, capped to a reasonable size for perf.
    const MAX_DIM = 720;
    const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
    const W = Math.max(1, Math.round(img.width * scale));
    const H = Math.max(1, Math.round(img.height * scale));
    canvas.width = W;
    canvas.height = H;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);

    // 0 = no effect, 1 = full effect.
    const t = Math.max(0, Math.min(1, goal / 100));

    // ----- 1. Per-row horizontal pinch on the lower face -----
    // Top region (above this y) is copied untouched.
    const PINCH_START = Math.round(H * 0.55);
    // Max horizontal contraction at the bottom of the image (per side).
    // Up to ~9% on each side (=> ~18% narrower at the chin) at goal=100. Gentle, not cartoonish.
    const MAX_PINCH = 0.09 * t;

    // Top half: straight copy, 1:1 pixel mapping. Eyes & mouth don't move.
    ctx.drawImage(img, 0, 0, img.width, PINCH_START / scale, 0, 0, W, PINCH_START);

    // Lower half: draw row by row with horizontal scale that ramps from 1 (at PINCH_START) to
    // (1 - 2*MAX_PINCH) at the bottom. Use a smooth ease-in curve so the transition is invisible.
    const lowerRows = H - PINCH_START;
    if (lowerRows > 0 && MAX_PINCH > 0) {
      // We sample a few-pixel-tall strip per draw call to keep this fast (~120 calls max).
      const STRIP = 2;
      for (let yDst = PINCH_START; yDst < H; yDst += STRIP) {
        const stripH = Math.min(STRIP, H - yDst);
        // u: 0 at PINCH_START, 1 at the very bottom
        const u = (yDst - PINCH_START) / Math.max(1, lowerRows - 1);
        // ease-in (cubic) so the pinch starts very gently and grows toward the chin
        const ease = u * u * u;
        const pinch = MAX_PINCH * ease;
        const newW = W * (1 - 2 * pinch);
        const dx = (W - newW) / 2;
        const sy = yDst / scale;
        const sh = stripH / scale;
        ctx.drawImage(
          img,
          0, sy, img.width, sh,
          dx, yDst, newW, stripH
        );
      }
    } else if (lowerRows > 0) {
      // No effect — straight copy of bottom too.
      ctx.drawImage(
        img,
        0, PINCH_START / scale, img.width, lowerRows / scale,
        0, PINCH_START, W, lowerRows
      );
    }

    // ----- 2. Soft shadow contour overlays (multiply blend) -----
    // Strength scales with t. We never go above ~0.32 alpha so it remains subtle and never
    // turns the face into a "blurry blob" — the underlying pixels always show through.
    if (t > 0.001) {
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';

      // Helper: paint a soft round shadow at (cx, cy) with radius r and peak alpha a.
      const shadow = (cxFrac, cyFrac, rFrac, alpha) => {
        const cx = W * cxFrac;
        const cy = H * cyFrac;
        const r = Math.max(W, H) * rFrac;
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        // Multiply blend: white = no change, dark grey = darker.
        const dark = Math.round(255 - 255 * alpha);
        grad.addColorStop(0, `rgba(${dark},${dark},${dark},1)`);
        grad.addColorStop(1, 'rgba(255,255,255,1)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, W, H);
      };

      // Cheek hollow shadows (under cheekbones, on either side). Peak ~y=62%.
      shadow(0.27, 0.62, 0.18, 0.22 * t);
      shadow(0.73, 0.62, 0.18, 0.22 * t);

      // Temple hollows (subtle, higher up).
      shadow(0.13, 0.32, 0.16, 0.14 * t);
      shadow(0.87, 0.32, 0.16, 0.14 * t);

      // Under-jaw shadow — anchors the new pointed chin.
      shadow(0.50, 0.92, 0.30, 0.28 * t);

      ctx.restore();
    }

    // ----- 3. A whisper of contrast so the contouring reads as a defined jawline rather than
    // just a darker patch. Done with a global multiply of a near-white gradient — extremely
    // subtle (max ~6% darkening at the very edges). -----
    if (t > 0.001) {
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';
      const v = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, Math.max(W, H) * 0.7);
      const edgeAlpha = 0.06 * t;
      const dark = Math.round(255 - 255 * edgeAlpha);
      v.addColorStop(0, 'rgba(255,255,255,1)');
      v.addColorStop(1, `rgba(${dark},${dark},${dark},1)`);
      ctx.fillStyle = v;
      ctx.fillRect(0, 0, W, H);
      ctx.restore();
    }
  }, [imgLoaded, goal]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      data-testid="debloat-canvas"
    />
  );
}
